import { h } from 'preact';
import { UrlUnfurledData } from 'core_main/pubnub/types';
import UrlPreview from './UrlPreview';
import styles from './urlPreview.scss';

/**
 * Props for UrlPreviews component
 */
interface UrlPreviewsProps {
  /** Array of URL unfurled preview data to display */
  previews: UrlUnfurledData[];
  /** Original message direction ('in' for inbound from agent, 'out' for outbound from customer) */
  direction: 'in' | 'out';
}

/**
 * UrlPreviews Component
 *
 * Container component that renders multiple URL preview cards.
 * Used to display all URL previews associated with a single message.
 *
 * Features:
 * - Renders multiple UrlPreview components in a vertical stack
 * - Returns null if no previews are provided (graceful handling)
 * - Each preview is keyed by URL and index for React reconciliation
 * - Applies consistent spacing between previews
 * - Passes original message direction to each preview
 *
 * @param previews - Array of URL unfurled data objects to render
 * @param direction - Original message direction ('in' for inbound from agent, 'out' for outbound from customer)
 *
 * @example
 * ```tsx
 * <UrlPreviews
 *   previews={[
 *     { url: 'https://example.com', title: 'Example' },
 *     { url: 'https://another.com', title: 'Another' }
 *   ]}
 *   direction="out"
 * />
 * ```
 */
const UrlPreviews = ({ previews, direction }: UrlPreviewsProps) => {
  if (!previews?.length) {
    return null;
  }

  return (
    <div className={styles.urlPreviewsContainer} data-direction={direction}>
      {previews.map((preview, index) => (
        <UrlPreview key={`${preview.url}-${index}`} preview={preview} direction={direction} />
      ))}
    </div>
  );
};

export default UrlPreviews;
